import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ComposedChart, Bar, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { BillingTrends } from '@/api/billing/billing.types';
import { TrendingUp, TrendingDown, Minus, BarChart3, Euro, Phone } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface TrendsChartProps {
  trends: BillingTrends | null;
  isLoading: boolean;
  onError: (error: string) => void;
  className?: string;
  trendsMonths: number;
  onTrendsMonthsChange: (months: number) => void;
}

const getTrendVariant = (trend?: string) => {
  switch (trend?.toLowerCase()) {
    case 'increasing':
      return 'default';
    case 'decreasing':
      return 'destructive';
    default:
      return 'secondary';
  }
};

const getTrendIcon = (trend?: string) => {
  switch (trend?.toLowerCase()) {
    case 'increasing':
      return TrendingUp;
    case 'decreasing':
      return TrendingDown;
    default:
      return Minus;
  }
};

const getTrendColor = (trend?: string) => {
  switch (trend?.toLowerCase()) {
    case 'increasing':
      return 'hsl(var(--primary))';
    case 'decreasing':
      return 'hsl(var(--destructive))';
    default:
      return 'hsl(var(--muted-foreground))';
  }
};

export const TrendsChart = ({ trends, isLoading, onError, className, trendsMonths, onTrendsMonthsChange }: TrendsChartProps) => {
  if (isLoading) {
    return (
      <Card className={cn("h-full shadow-sm", className)}>
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-lg" />
              <Skeleton className="h-5 w-32" />
            </div>
            <Skeleton className="h-9 w-[130px] rounded-md" />
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="h-[280px] relative">
            <Skeleton className="h-full w-full rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!trends?.monthlyData || trends.monthlyData.length === 0) {
    return (
      <Card className={cn("h-full shadow-sm", className)}>
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <BarChart3 className="h-4 w-4 text-primary" />
            </div>
            <div className="space-y-0.5">
              <CardTitle className="text-base leading-none">Monthly Trends</CardTitle>
              <CardDescription className="text-xs text-muted-foreground/70 leading-none">
                Calls and costs over time
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[calc(100%-73px)]">
          <div className="text-center space-y-3">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <BarChart3 className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium mb-1">No Trend Data</p>
              <p className="text-xs text-muted-foreground">Monthly trend data will appear here as usage accumulates</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg p-4">
          <p className="font-medium mb-2">{label}</p>
          <div className="space-y-2">
            {payload.map((entry: any, index: number) => (
              <div key={index} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sm">{entry.dataKey === 'calls' ? 'Calls' : 'Cost (€)'}</span>
                </div>
                <span className="font-medium">
                  {entry.dataKey === 'calls' 
                    ? entry.value.toLocaleString() 
                    : `€${entry.value.toFixed(2)}`
                  }
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  const CallsTrendIcon = getTrendIcon(trends.growth?.callsTrend);
  const CostTrendIcon = getTrendIcon(trends.growth?.costTrend);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut", delay: 0.15 }}
      className={className}
    >
      <Card className="h-full shadow-sm">
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <BarChart3 className="h-4 w-4 text-primary" />
              </div>
              <div className="space-y-0.5">
                <CardTitle className="text-base leading-none">Monthly Trends</CardTitle>
                <CardDescription className="text-xs text-muted-foreground/70 leading-none">
                  Calls and costs over time
                </CardDescription>
              </div>
            </div>
            <Select 
              value={trendsMonths.toString()} 
              onValueChange={(value) => onTrendsMonthsChange(parseInt(value))}
            >
              <SelectTrigger className="w-[130px] h-9">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 Months</SelectItem>
                <SelectItem value="6">6 Months</SelectItem>
                <SelectItem value="12">12 Months</SelectItem>
                <SelectItem value="24">24 Months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        
        <CardContent className="pt-6">
          {/* Chart */}
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart 
                data={trends.monthlyData} 
                margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
              >
                <defs>
                  <linearGradient id="callsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                  </linearGradient>
                </defs>
                
                <XAxis 
                  dataKey="month" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                  dy={5}
                />
                <YAxis 
                  yAxisId="calls"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                  dx={-5}
                />
                <YAxis 
                  yAxisId="cost"
                  orientation="right"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                  dx={5}
                />
                
                <Tooltip content={<CustomTooltip />} />
                
                <Bar 
                  yAxisId="calls"
                  dataKey="calls" 
                  fill="url(#callsGradient)"
                  radius={[4, 4, 0, 0]}
                  stroke="hsl(var(--primary))"
                  strokeWidth={1}
                />
                
                <Line 
                  yAxisId="cost"
                  type="monotone" 
                  dataKey="cost" 
                  stroke="hsl(39 84% 56%)"
                  strokeWidth={3}
                  dot={{ 
                    fill: 'hsl(39 84% 56%)', 
                    strokeWidth: 2, 
                    stroke: 'hsl(var(--background))',
                    r: 4 
                  }}
                  activeDot={{ 
                    r: 6, 
                    fill: 'hsl(39 84% 56%)',
                    stroke: 'hsl(var(--background))',
                    strokeWidth: 2
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};